import { forwardRef, type ImgHTMLAttributes } from "react";

// Shape emitted by vite-imagetools when a module is imported with the
// `?as=picture` directive. Kept inline rather than imported from the dep so
// we don't reach into a transitive module's dist folder.
export interface Picture {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
}

// Drop-in <img> replacement for images processed by vite-imagetools with the
// `?as=picture` directive. Renders a <picture> with one <source> per emitted
// format (AVIF / WebP / JPEG/PNG fallback), plus the underlying <img> with
// the intrinsic width/height the plugin extracted from the file — which
// satisfies the "image elements do not have explicit width and height" audit
// and prevents layout shift because the browser reserves the correct aspect
// ratio box before the bytes arrive.

// The imagetools Picture shape is
// { sources: { [format]: srcsetString }, img: { src, w, h } }.

type ResponsiveImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet" | "width" | "height"
> & {
  picture: Picture;
  sizes: string;
  alt: string;
  /** When true, marks as the LCP image (eager + high priority). */
  priority?: boolean;
  /** Override intrinsic width/height (e.g. for fixed-size avatars). */
  width?: number | string;
  height?: number | string;
};

// Map imagetools output keys to the right MIME type. `jpg` is the extension
// used by the plugin even when it originated from `jpeg`.
const MIME: Record<string, string> = {
  avif: "image/avif",
  webp: "image/webp",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};

// Stable ordering — browsers use the first matching <source>, so put the
// most efficient format first.
const FORMAT_ORDER = ["avif", "webp", "jpg", "jpeg", "png"] as const;

export const ResponsiveImage = forwardRef<HTMLImageElement, ResponsiveImageProps>(
  function ResponsiveImage(
    { picture, sizes, alt, priority, className, width, height, loading, ...rest },
    ref,
  ) {
    const entries = FORMAT_ORDER.filter((fmt) => picture.sources[fmt]);
    const loadingAttr = priority ? "eager" : (loading ?? "lazy");
    // fetchpriority isn't in React's typings until React 19; cast is safe.
    const priorityAttrs = priority
      ? ({ fetchpriority: "high" } as unknown as Record<string, string>)
      : {};
    return (
      <picture>
        {entries.map((fmt) => (
          <source
            key={fmt}
            type={MIME[fmt]}
            srcSet={picture.sources[fmt]}
            sizes={sizes}
          />
        ))}
        <img
          ref={ref}
          src={picture.img.src}
          srcSet={picture.sources.jpg || picture.sources.png}
          sizes={sizes}
          width={width ?? picture.img.w}
          height={height ?? picture.img.h}
          alt={alt}
          loading={loadingAttr}
          decoding={priority ? "sync" : "async"}
          className={className}
          {...priorityAttrs}
          {...rest}
        />
      </picture>
    );
  },
);

// Drop-in for remote images (Supabase / Unsplash) that we can't run through
// vite-imagetools. Emits a single <img> with srcset/sizes and explicit
// width/height so CLS stays zero and the browser can pick the right byte
// count per device.

type RemoteImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "width" | "height"
> & {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
};

export const RemoteImage = forwardRef<HTMLImageElement, RemoteImageProps>(
  function RemoteImage(
    { src, srcSet, sizes, alt, width, height, priority, loading, className, ...rest },
    ref,
  ) {
    const loadingAttr = priority ? "eager" : (loading ?? "lazy");
    const priorityAttrs = priority
      ? ({ fetchpriority: "high" } as unknown as Record<string, string>)
      : {};
    return (
      <img
        ref={ref}
        src={src}
        // Empty srcSet (""/undefined) causes some browsers to emit a
        // warning — only pass the attribute when a real srcset is provided.
        {...(srcSet ? { srcSet } : {})}
        {...(srcSet && sizes ? { sizes } : {})}
        width={width}
        height={height}
        alt={alt}
        loading={loadingAttr}
        decoding={priority ? "sync" : "async"}
        className={className}
        {...priorityAttrs}
        {...rest}
      />
    );
  },
);
